import {
  doc,
  collection,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  getDoc,
  onSnapshot,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  query,
  where,
  orderBy,
  getDocs,
  limit,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from './firebaseService';
import { compressImagesDetailed, UnsendableImagesError } from '../utils/imageCompression';
import { CollaborativeMap, CollaborativeMapMember, SharedPackingList, AppUser, CollabMessage } from '../types';

function generateShareToken(): string {
  return Math.random().toString(16).slice(2, 10);
}

export async function createCollaborativeMap(
  user: AppUser,
  name: string,
  description?: string
): Promise<string> {
  const mapRef = doc(collection(db, 'collaborative_maps'));
  const member: CollaborativeMapMember = {
    uid: user.uid,
    displayName: user.displayName || user.email || 'Ukjent',
    photoURL: user.photoURL ?? null,
    role: 'owner',
    joinedAt: Date.now(),
  };
  const now = Date.now();
  const mapData = {
    createdBy: user.uid,
    creatorName: user.displayName || user.email || 'Ukjent',
    name,
    description: description || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    shareToken: generateShareToken(),
    isLinkShareEnabled: true,
    spotIds: [] as string[],
    routeIds: [] as string[],
    members: [member],
    memberUids: [user.uid],
    _clientCreatedAt: now,
  };
  await setDoc(mapRef, mapData);
  return mapRef.id;
}

// Innholds-stempel («nytt i Mitt»-badge for andre medlemmer): hvem la til/endret sist,
// og når. Speiler chat-mønsteret (lastMessageBy/AtMs). Egen-endringer teller ikke som
// «nytt» for en selv (hasUnseenCollabContent filtrerer på uid).
function contentTouch(): Record<string, unknown> {
  const uid = auth.currentUser?.uid;
  return {
    contentUpdatedAtMs: Date.now(),
    ...(uid ? { contentUpdatedBy: uid } : {}),
  };
}

export async function addSpotToMap(mapId: string, spotId: string): Promise<void> {
  const ref = doc(db, 'collaborative_maps', mapId);
  await updateDoc(ref, {
    spotIds: arrayUnion(spotId),
    updatedAt: serverTimestamp(),
    ...contentTouch(),
  });
}

export async function removeSpotFromMap(mapId: string, spotId: string): Promise<void> {
  const ref = doc(db, 'collaborative_maps', mapId);
  await updateDoc(ref, {
    spotIds: arrayRemove(spotId),
    updatedAt: serverTimestamp(),
  });
}

export async function addRouteToMap(mapId: string, routeId: string): Promise<void> {
  const ref = doc(db, 'collaborative_maps', mapId);
  await updateDoc(ref, {
    routeIds: arrayUnion(routeId),
    updatedAt: serverTimestamp(),
    ...contentTouch(),
  });
}

export async function removeRouteFromMap(mapId: string, routeId: string): Promise<void> {
  const ref = doc(db, 'collaborative_maps', mapId);
  await updateDoc(ref, {
    routeIds: arrayRemove(routeId),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Slår opp en e-postadresse og legger eksisterende brukere rett inn i kartet.
 *  - Finnes brukeren allerede → legges inn som editor ('ok'). Cloud Function
 *    `onCollabMapMemberAdded` sender in-app + push-varsel.
 *  - Finnes brukeren IKKE → returnerer 'not_found' UTEN bivirkning. Kalleren bør
 *    bekrefte med brukeren og deretter kalle `sendEmailInvite` for å sende
 *    invitasjons-e-post.
 */
export async function inviteUserByEmail(
  mapId: string,
  email: string
): Promise<'ok' | 'already_member' | 'not_found' | 'error'> {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) return 'error';

  const mapRef = doc(db, 'collaborative_maps', mapId);
  const mapSnap = await getDoc(mapRef);
  if (!mapSnap.exists()) return 'error';
  const mapData = mapSnap.data() as CollaborativeMap;

  const usersQuery = query(collection(db, 'users'), where('email', '==', normalizedEmail), limit(1));
  const snap = await getDocs(usersQuery);

  if (snap.empty) return 'not_found';

  // Eksisterende bruker → legg rett inn
  const userDoc = snap.docs[0];
  const invitedUid = userDoc.id;
  const userData = userDoc.data();
  if (mapData.memberUids?.includes(invitedUid)) return 'already_member';

  const newMember: CollaborativeMapMember = {
    uid: invitedUid,
    displayName: userData.displayName || userData.email || 'Ukjent',
    photoURL: userData.photoURL ?? null,
    role: 'editor',
    joinedAt: Date.now(),
  };
  await updateDoc(mapRef, {
    members: arrayUnion(newMember),
    memberUids: arrayUnion(invitedUid),
    updatedAt: serverTimestamp(),
  });
  return 'ok';
}

/**
 * Sender en invitasjons-e-post til en adresse uten konto (etter at brukeren har
 * bekreftet at de vil det). Oppretter en `pending_invites`-post som
 * `onPendingInviteCreated` sender profilert e-post for, og som `onNewUserCreated`
 * løser inn til automatisk tilgang når mottakeren registrerer seg med e-posten.
 * Dedupliserer mot en allerede ventende invitasjon for samme kart.
 */
export async function sendEmailInvite(
  mapId: string,
  email: string,
  inviter?: { uid: string; displayName?: string | null; lang?: string }
): Promise<'invited' | 'error'> {
  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) return 'error';

  const mapRef = doc(db, 'collaborative_maps', mapId);
  const mapSnap = await getDoc(mapRef);
  if (!mapSnap.exists()) return 'error';
  const mapData = mapSnap.data() as CollaborativeMap;

  const existingQuery = query(
    collection(db, 'pending_invites'),
    where('email', '==', normalizedEmail),
  );
  const existingSnap = await getDocs(existingQuery);
  const alreadyInvited = existingSnap.docs.some(
    d => d.data().mapId === mapId && d.data().status === 'pending'
  );
  if (alreadyInvited) return 'invited';

  await addDoc(collection(db, 'pending_invites'), {
    email: normalizedEmail,
    mapId,
    mapName: mapData.name || 'et delt kart',
    shareToken: mapData.shareToken || null,
    invitedByUid: inviter?.uid || mapData.createdBy || null,
    invitedByName: inviter?.displayName || mapData.creatorName || 'Noen',
    role: 'editor',
    status: 'pending',
    lang: inviter?.lang || 'no',
    createdAt: serverTimestamp(),
    emailSentAt: null,
  });
  return 'invited';
}

/**
 * Abonnerer på ventende (ikke-aksepterte) invitasjoner for et kart, slik at
 * eieren ser hvem som er invitert men ikke har opprettet konto ennå.
 */
export function subscribeToPendingInvites(
  mapId: string,
  callback: (emails: string[]) => void
): () => void {
  const q = query(collection(db, 'pending_invites'), where('mapId', '==', mapId));
  return onSnapshot(
    q,
    snap => {
      const emails = snap.docs
        .filter(d => d.data().status === 'pending')
        .map(d => d.data().email as string);
      callback(Array.from(new Set(emails)));
    },
    () => callback([])
  );
}

// ---------------------------------------------------------------------------
// Gruppe-chat i samarbeidskart. Gjenbruker meldingsformat, bildeopplasting og
// oversettelse fra tester/admin-chatten, men er N-personers: ulest spores per
// medlem via `lastReadBy[uid]` på kart-dokumentet.
// ---------------------------------------------------------------------------

export async function sendCollabMessage(
  mapId: string,
  sender: AppUser,
  text: string,
  imageFiles: File[] = [],
  translation?: { translatedText: string; translatedTo: string }
): Promise<void> {
  const trimmed = text.trim();
  if (!trimmed && imageFiles.length === 0) return;

  const imageUrls: string[] = [];
  if (imageFiles.length) {
    // compressImagesDetailed, ikke compressImages: den korte varianten svelger filer
    // den ikke får komprimert, og da ble det skrevet en melding uten tekst og uten
    // bilder — en blank boble i chatten som avsenderen tror ble sendt.
    const { files: compressed, skipped } = await compressImagesDetailed(imageFiles);
    if (!trimmed && compressed.length === 0) throw new UnsendableImagesError(skipped);
    for (const file of compressed) {
      const fileName = `${sender.uid}_${Date.now()}_${file.name}`;
      const imageRef = ref(storage, `collab_messages/${mapId}/${fileName}`);
      const snap = await uploadBytes(imageRef, file);
      imageUrls.push(await getDownloadURL(snap.ref));
    }
  }

  const now = Date.now();
  const messageRef = doc(collection(db, 'collaborative_maps', mapId, 'messages'));
  await setDoc(messageRef, {
    senderId: sender.uid,
    senderName: sender.displayName || sender.email || 'Ukjent',
    senderPhotoURL: sender.photoURL ?? null,
    text: trimmed,
    imageUrls,
    createdAt: serverTimestamp(),
    createdAtMs: now,
    ...(translation && { translatedText: translation.translatedText, translatedTo: translation.translatedTo }),
  });

  // Chat-metadata + markér avsender som lest (de skrev jo nettopp)
  await updateDoc(doc(db, 'collaborative_maps', mapId), {
    lastMessageText: trimmed || '📷',
    lastMessageBy: sender.uid,
    lastMessageAtMs: now,
    updatedAt: serverTimestamp(),
    [`lastReadBy.${sender.uid}`]: now,
  });
}

export function subscribeToCollabMessages(
  mapId: string,
  callback: (messages: CollabMessage[]) => void
): () => void {
  const q = query(collection(db, 'collaborative_maps', mapId, 'messages'), orderBy('createdAtMs', 'asc'));
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => ({ ...(d.data() as Omit<CollabMessage, 'id'>), id: d.id }))),
    () => {
      // Fallback uten ordering hvis indeks/tillatelse feiler midlertidig
      const q2 = query(collection(db, 'collaborative_maps', mapId, 'messages'));
      onSnapshot(q2, snap => {
        const msgs = snap.docs.map(d => ({ ...(d.data() as Omit<CollabMessage, 'id'>), id: d.id }));
        msgs.sort((a, b) => (a.createdAtMs || 0) - (b.createdAtMs || 0));
        callback(msgs);
      }, () => callback([]));
    }
  );
}

export async function markCollabThreadAsRead(mapId: string, uid: string): Promise<void> {
  await updateDoc(doc(db, 'collaborative_maps', mapId), {
    [`lastReadBy.${uid}`]: Date.now(),
  });
  // Nullstill den sammenslåtte chat-varsel-oppføringen (functions skriver den til
  // users/{uid}/notifications/collab_msg_{mapId}). Best effort — doc-en finnes bare
  // hvis noen har skrevet i chatten mens brukeren var borte.
  try {
    await updateDoc(doc(db, 'users', uid, 'notifications', `collab_msg_${mapId}`), {
      isRead: true,
      unreadCount: 0,
    });
  } catch {
    // Ingen varsel-doc for dette kartet — helt greit.
  }
}

/**
 * Ren hjelpefunksjon: har `uid` uleste meldinger i kartets chat? Sann når siste
 * melding er nyere enn medlemmets `lastReadBy`, og ikke sendt av dem selv.
 */
export function hasUnreadCollabChat(map: Pick<CollaborativeMap, 'lastMessageAtMs' | 'lastMessageBy' | 'lastReadBy'>, uid: string): boolean {
  if (!map.lastMessageAtMs) return false;
  if (map.lastMessageBy === uid) return false;
  const lastRead = map.lastReadBy?.[uid] || 0;
  return map.lastMessageAtMs > lastRead;
}

/**
 * Ren hjelpefunksjon: har et annet medlem lagt til/endret INNHOLD (plasser/ruter/
 * pakkelister) siden `uid` sist så Mitt? Speiler hasUnreadCollabChat.
 */
export function hasUnseenCollabContent(
  map: Pick<CollaborativeMap, 'contentUpdatedAtMs' | 'contentUpdatedBy' | 'contentSeenBy'>,
  uid: string,
): boolean {
  if (!map.contentUpdatedAtMs) return false;
  if (map.contentUpdatedBy === uid) return false;
  const lastSeen = map.contentSeenBy?.[uid] || 0;
  return map.contentUpdatedAtMs > lastSeen;
}

/** Marker at `uid` har sett kartets innhold (nullstiller «nytt i Mitt»-badgen). */
export async function markCollabContentSeen(mapId: string, uid: string): Promise<void> {
  await updateDoc(doc(db, 'collaborative_maps', mapId), {
    [`contentSeenBy.${uid}`]: Date.now(),
  });
}

export async function joinMapByToken(
  mapId: string,
  token: string,
  user: AppUser
): Promise<'ok' | 'invalid_token' | 'already_member'> {
  const mapRef = doc(db, 'collaborative_maps', mapId);
  const mapSnap = await getDoc(mapRef);
  if (!mapSnap.exists()) return 'invalid_token';

  const mapData = mapSnap.data() as CollaborativeMap;
  if (mapData.shareToken !== token) return 'invalid_token';
  if (mapData.memberUids?.includes(user.uid)) return 'already_member';

  const newMember: CollaborativeMapMember = {
    uid: user.uid,
    displayName: user.displayName || user.email || 'Ukjent',
    photoURL: user.photoURL ?? null,
    role: 'editor',
    joinedAt: Date.now(),
  };

  await updateDoc(mapRef, {
    members: arrayUnion(newMember),
    memberUids: arrayUnion(user.uid),
    updatedAt: serverTimestamp(),
  });
  return 'ok';
}

export async function removeMemberFromMap(mapId: string, targetUid: string): Promise<void> {
  const mapRef = doc(db, 'collaborative_maps', mapId);
  const mapSnap = await getDoc(mapRef);
  if (!mapSnap.exists()) return;

  const mapData = mapSnap.data() as CollaborativeMap;
  const memberToRemove = mapData.members?.find(m => m.uid === targetUid);
  if (!memberToRemove) return;

  await updateDoc(mapRef, {
    members: arrayRemove(memberToRemove),
    memberUids: arrayRemove(targetUid),
    updatedAt: serverTimestamp(),
  });
}

export async function leaveOrDeleteMap(
  mapId: string,
  userId: string,
  isOwner: boolean
): Promise<void> {
  const mapRef = doc(db, 'collaborative_maps', mapId);
  if (isOwner) {
    await deleteDoc(mapRef);
  } else {
    await removeMemberFromMap(mapId, userId);
  }
}

export function subscribeToUserCollaborativeMaps(
  userId: string,
  callback: (maps: CollaborativeMap[]) => void
): () => void {
  const q = query(
    collection(db, 'collaborative_maps'),
    where('memberUids', 'array-contains', userId)
  );
  return onSnapshot(q, (snap) => {
    const maps: CollaborativeMap[] = snap.docs.map(d => {
      const data = d.data();
      return {
        id: d.id,
        createdBy: data.createdBy,
        creatorName: data.creatorName,
        name: data.name,
        description: data.description || '',
        createdAt: data.createdAt?.toMillis?.() ?? Date.now(),
        updatedAt: data.updatedAt?.toMillis?.() ?? Date.now(),
        shareToken: data.shareToken,
        isLinkShareEnabled: data.isLinkShareEnabled ?? true,
        spotIds: data.spotIds || [],
        routeIds: data.routeIds || [],
        members: data.members || [],
        memberUids: data.memberUids || [],
        packingLists: data.packingLists || {},
      } as CollaborativeMap;
    });
    callback(maps);
  }, () => callback([]));
}

// --- Pakkelister i samarbeidsmapper ---

export async function addPackingListToMap(
  mapId: string,
  list: SharedPackingList
): Promise<void> {
  const ref = doc(db, 'collaborative_maps', mapId);
  await updateDoc(ref, {
    [`packingLists.${list.id}`]: list,
    updatedAt: serverTimestamp(),
    ...contentTouch(),
  });
}

export async function removePackingListFromMap(
  mapId: string,
  listId: string
): Promise<void> {
  const ref = doc(db, 'collaborative_maps', mapId);
  await updateDoc(ref, {
    [`packingLists.${listId}`]: deleteField(),
    updatedAt: serverTimestamp(),
  });
}

export async function updatePackingListInMap(
  mapId: string,
  listId: string,
  updatedList: SharedPackingList
): Promise<void> {
  const ref = doc(db, 'collaborative_maps', mapId);
  await updateDoc(ref, {
    [`packingLists.${listId}`]: { ...updatedList, updatedAt: Date.now() },
    updatedAt: serverTimestamp(),
    ...contentTouch(),
  });
}
